import throttle from "lodash/throttle";

export default function (_vue) {
  return function (p) {
    // Vue pass-through
    let data = null;
    let vue = _vue;

    // interface constants
    const sliderOffset = 10;
    const sliderHeight = 24;
    const sliderPadding = 10;
    const sliderMargin = 6;
    const sliderWidth = 8;

    // Setup image and canvas
    let img, canvas;
    let imageWidth = 100;
    let prevWidth = 100;

    // Rectangle variables
    let x1 = 14;
    let y1 = 3;
    let w1 = 35;
    let h1 = 43;

    // Interface State
    let imageDragging = false;
    let sliderDragging = false;
    let sliderValue = 50;

    //--------------------------------------------------
    //              P5.js EVENT LOOP
    //--------------------------------------------------

    p.setup = function () {
      p.frameRate(60);
      canvas = p.createCanvas(100, 800);
      p.windowResized();
    };

    p.draw = function () {
      if (!img) return;
      // Draw  interface elements
      p.clear();
      drawHeroImage();
      drawSlider();
    };

    //--------------------------------------------------
    //              EVENT HANDLERS
    //--------------------------------------------------

    p.windowResized = throttle(function () {
      p.resizeCanvas(0, canvas.height);
      imageWidth = canvas.parent().clientWidth;

      if (data) updateImage();

      if (prevWidth) {
        let ratio = imageWidth / prevWidth;
        if (x1) x1 = x1 * ratio;
        if (y1) y1 = y1 * ratio;
        if (w1) w1 = w1 * ratio;
        if (h1) h1 = h1 * ratio;
      }

      prevWidth = imageWidth;
      doCalculations();
    }, 500);

    p.mousePressed = function () {
      if (inImage()) {
        x1 = p.mouseX;
        y1 = p.mouseY;
        w1 = 0;
        h1 = 0;
        imageDragging = true;
        doCalculations();

        return false;
      }

      if (inSlider()) {
        sliderDragging = true;
        sliderValue = mapOffset(p.mouseX);
        doCalculations();

        return false;
      }
    };

    p.mouseDragged = function () {
      if (imageDragging && inImage()) {
        w1 = p.mouseX - x1;
        h1 = p.mouseY - y1;
        doCalculations();

        return false;
      }
      if (sliderDragging && inSlider()) {
        sliderValue = mapOffset(p.mouseX);
        doCalculations();
        return false;
      }
    };

    p.mouseReleased = function () {
      imageDragging = false;
      sliderDragging = false;
    };

    //--------------------------------------------------
    //             EVENT HELPERS
    //--------------------------------------------------

    function inImage() {
      if (!img) return false;

      const x = p.mouseX;
      const y = p.mouseY;
      if (x < 0 || x > img.width) {
        return false;
      }
      if (y < 0 || y > img.height) {
        return false;
      }
      return true;
    }

    function inSlider() {
      if (!img) return false;

      const x = p.mouseX;
      const y = p.mouseY;
      if (x < 0 || x > img.width) {
        return false;
      }
      if (
        y < sliderY() - sliderMargin ||
        y > sliderY() + sliderHeight + sliderMargin
      ) {
        return false;
      }
      return true;
    }

    //--------------------------------------------------
    //             INTERFACE HELPERS
    //--------------------------------------------------
    function sliderY() {
      const i = img?.height || 0;
      return i + sliderOffset;
    }

    function calcOtherX() {
      return (
        imageWidth -
        x1 -
        (imageWidth / 2 - imageWidth * (sliderValue / 100)) * 2
      );
    }

    function mapOffset(x) {
      return p.map(x, 0, imageWidth, 0, 100, true);
    }

    function doCalculations() {
      if (img) {
        let leftX = p.min(x1, x1 + w1);
        let rightX = p.min(calcOtherX(), calcOtherX() - w1);
        let sourceY = p.min(y1, y1 + h1);
        let sourceWidth = p.abs(w1);
        let sourceHeight = p.abs(h1);

        let lEye = img.get(leftX, sourceY, sourceWidth, sourceHeight);
        let rEye = img.get(rightX, sourceY, sourceWidth, sourceHeight);

        const l = p.round((100 * leftX) / imageWidth, 3);
        const r = p.round((100 * rightX) / imageWidth, 3);
        const y = p.round((100 * sourceY) / img.height, 3);
        const w = p.round((100 * sourceWidth) / imageWidth, 3);
        const h = p.round((100 * sourceHeight) / img.height, 3);

        const coords = { l: l, r: r, y: y, w: w, h: h };
        vue.$emit("update:eyes", { l: lEye, r: rEye, coords: coords });
      }
    }

    function calculateBoxes(cw, _lcoords, _rcoords) {
      const lcoords = _lcoords.split(",");
      const rcoords = _rcoords.split(",");
      const ratio = imageWidth / cw;

      x1 = lcoords[0] * ratio;
      y1 = lcoords[1] * ratio;
      w1 = lcoords[2] * ratio;
      h1 = lcoords[3] * ratio;

      const r = +rcoords[0];
      const l = +lcoords[0] + +lcoords[2];
      sliderValue = -(50 * (-r - l)) / cw;
    }

    function drawSlider() {
      const sliderPos = imageWidth * (sliderValue / 100) - sliderWidth / 2;
      p.push();
      p.noStroke();
      p.translate(0, sliderY());
      p.fill(120);
      p.rect(0, sliderPadding, imageWidth, sliderHeight - sliderPadding * 2);
      p.fill("#862e2e");
      p.rect(sliderPos, 0, sliderWidth, sliderHeight, sliderWidth / 2);
      p.pop();
    }

    function drawHeroImage() {
      p.image(img, 0, 0);
      p.stroke(255, 255, 255, 128);
      p.fill(0, 0, 0, 128);
      p.rect(x1, y1, w1, h1);
      p.rect(calcOtherX(), y1, -w1, h1);
    }

    //--------------------------------------------------
    //             DATA MANAGEMENT FUNCTIONS
    //--------------------------------------------------

    /**
     * Handle updates from the Vue.js data
     * @param  {Object} _data The passed-in parameters
     */
    p.dataChanged = function (_data, olddata) {
      data = _data;
      if (olddata.id != _data.id) {
        updateImage();
      }
      if (olddata.canvasWidth != _data.canvasWidth) {
        calculateBoxes(_data.canvasWidth, data.lcoords, data.rcoords);
      }
    };

    function updateImage() {
      let url = data.id + "/full/" + imageWidth + ",/0/default.jpg";
      p.loadImage(url, handleNewImage.bind(this));
    }
    function handleNewImage(_img) {
      img = _img;
      doCalculations();
      let w = canvas.parent().clientWidth;
      let h = sliderY() + sliderHeight;
      p.resizeCanvas(w, h);
    }
  };
}
